import { type Validator, useTranslate } from 'ra-core';
import { useId } from 'react';
import { FileInput } from '../../../components/admin/file-input';
import { ImageField } from '../../../components/admin/image-field';
import { TextInput } from '../../../components/admin/text-input';
import { UrlSourceInput } from '../../../components/custom/UrlSourceInput';
import { Label } from '../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import {
  maxFileSize,
  requiredIfFieldsAreBlank,
} from '../../../utils/validation';
import { SeverityAlert } from '../../Moonstar/SeverityAlert';

export type NudgeSlideTab = 'withContentAndImage' | 'withUrl';

const SLIDE_TABS: NudgeSlideTab[] = ['withContentAndImage', 'withUrl'];

const validationForContentAndImageSlide: Validator = requiredIfFieldsAreBlank(
  { everyField: ['image', 'content'] },
  'moonstar.nudges.slides.imageOrContentRequired'
);

const imageValidation = maxFileSize('5MB');
const imageValidators = [validationForContentAndImageSlide, imageValidation];

/**
 * The slide form inputs, shared by create and edit: a "Quick note" (image and / or content) or an "External Link"
 * (a content URL), picked with the radio buttons.
 */
export const NudgeSlideInputs = ({
  selectedTab,
  onTabChange,
}: {
  selectedTab: NudgeSlideTab;
  onTabChange: (tab: NudgeSlideTab) => void;
}) => {
  const translate = useTranslate();
  const id = useId();

  return (
    <>
      <RadioGroup
        value={selectedTab}
        onValueChange={(value) => onTabChange(value as NudgeSlideTab)}
        aria-label={translate('moonstar.nudges.slides.type.label')}
        className="flex flex-wrap gap-4"
      >
        {SLIDE_TABS.map((tab) => (
          <div key={tab} className="flex items-center gap-2">
            <RadioGroupItem value={tab} id={`${id}-${tab}`} />
            <Label htmlFor={`${id}-${tab}`} className="cursor-pointer">
              {translate(`moonstar.nudges.slides.type.${tab}`)}
            </Label>
          </div>
        ))}
      </RadioGroup>
      <div className="flex w-full max-w-[600px] flex-col gap-4">
        {selectedTab === 'withContentAndImage' && (
          <>
            <FileInput
              source="image"
              accept={{ 'image/*': ['.jpg', '.jpeg', '.png', '.gif'] }}
              validate={imageValidators}
            >
              <ImageField
                source="src"
                title="title"
                className="[&_img]:max-h-40 [&_img]:object-contain"
              />
            </FileInput>
            <TextInput
              multiline
              // Like the MUI `minRows={3}` (the shadcn textarea ignores `rows` and grows with its content)
              inputClassName="min-h-20 resize-y"
              source="content"
              validate={validationForContentAndImageSlide}
            />
          </>
        )}

        {selectedTab === 'withUrl' && (
          <>
            <SeverityAlert
              severity="warning"
              title={translate('moonstar.nudges.slides.externalLink.title')}
            >
              {translate('moonstar.nudges.slides.externalLink.description')}
            </SeverityAlert>
            <UrlSourceInput source="url" />
          </>
        )}
      </div>
    </>
  );
};
